import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { backendApi } from "@/api/backendApi";
import { Bell, CheckCircle, Briefcase, Eye, Clock, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";

export default function Notifications() {
  const queryClient = useQueryClient();

  const { data: notifications, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => backendApi.Notification.getAll(),
  });

  const markReadMutation = useMutation({
    mutationFn: (id) => backendApi.Notification.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
      queryClient.invalidateQueries(["notifications", "unread-count"]);
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => backendApi.Notification.markAll(),
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
      queryClient.invalidateQueries(["notifications", "unread-count"]);
    },
  });

  const getNotificationIcon = (type) => {
    switch (type) {
      case "applied":
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case "new_job":
        return <Briefcase className="w-5 h-5 text-orange-600" />;
      case "view":
        return <Eye className="w-5 h-5 text-purple-600" />;
      default:
        return <Bell className="w-5 h-5 text-blue-600" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case "applied":
        return "bg-green-100";
      case "new_job":
        return "bg-orange-100";
      case "view":
        return "bg-purple-100";
      default:
        return "bg-blue-100";
    }
  };

  const unreadCount = notifications?.filter((n) => !n.is_read).length || 0;

  return (
    <main className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-500 text-sm mt-1">
            Stay updated with your job applications and recruiter actions
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            onClick={() => markAllReadMutation.mutate()}
            className="flex items-center gap-2 text-[#2557a7] border-[#2557a7]/20 hover:bg-blue-50"
          >
            <Check className="w-4 h-4" />
            Mark all as read
          </Button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2557a7] mx-auto mb-4"></div>
            Loading notifications...
          </div>
        ) : notifications && notifications.length > 0 ? (
          <div className="divide-y divide-gray-50">
            {notifications.map((notif) => (
              <div
                key={notif.id}
                onClick={() => !notif.is_read && markReadMutation.mutate(notif.id)}
                className={`p-6 hover:bg-gray-50 transition-colors cursor-pointer flex gap-4 ${
                  !notif.is_read ? "bg-blue-50/30" : ""
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-xl ${getNotificationColor(
                    notif.type
                  )} flex items-center justify-center shrink-0 shadow-sm`}
                >
                  {getNotificationIcon(notif.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start gap-4">
                    <p
                      className={`text-base leading-snug ${
                        !notif.is_read
                          ? "font-semibold text-gray-900"
                          : "text-gray-600"
                      }`}
                    >
                      {notif.message}
                    </p>
                    {!notif.is_read && (
                      <div className="w-2 h-2 rounded-full bg-[#2557a7] mt-2 shrink-0"></div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {formatDistanceToNow(new Date(notif.created_at), {
                        addSuffix: true,
                      })}
                    </span>
                    {notif.is_read && (
                      <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">
                        Read
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-20 text-center flex flex-col items-center">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
              <Bell className="w-10 h-10 text-gray-200" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              You're all caught up! 🎉
            </h3>
            <p className="text-gray-500 max-w-sm mx-auto">
              Any new updates about your applications, recruiter searches, or job recommendations will appear here.
            </p>
            <Button
              className="mt-8 bg-[#2557a7] hover:bg-[#1e4687] text-white"
              onClick={() => (window.location.href = "/jobs/dashboard")}
            >
              Back to Dashboard
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
